import {
  faImage,
  faPaperPlane,
  faPaperclip,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../../../components/base/Button';
import TinymceEditor from '../../../components/base/TinymceEditor';
import EmailLayout from '../../../layouts/EmailLayout';
import { Card, Col, Form, Row, Spinner, Alert } from 'react-bootstrap';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// 📧 CONFIGURACIÓN POR DEFECTO - Extraída del modal
const DEFAULT_EMAIL_CONFIG = {
  server: 'mail.linuxcorp.cl',
  username: 'anubis@linuxcorp.cl',
  password: '4n00bies.$_240829',
  imapPort: 993,
  smtpPort: 465,
  ssl: true,
  displayName: 'Anubis AI'
};

// 📎 CONFIGURACIÓN DE ADJUNTOS
const DEFAULT_SIZE_LIMIT = 25 * 1024 * 1024; // 25MB por defecto
const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.rtf', '.csv', '.zip', '.rar', '.7z',
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.webp',
  '.mp3', '.wav', '.mp4', '.avi', '.mov', '.wmv'
];

// Interfaces para el formulario
interface EmailFormData {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
}

interface AttachmentFile {
  file: File;
  id: string;
  tempPath?: string;
  status: 'uploading' | 'uploaded' | 'error';
  error?: string;
}

interface AppConfig {
  sizeLimit: number;
  tempPath: string;
  allowedExtensions: string[];
}

// Función para validar emails
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Función para validar lista de emails separados por coma
const validateEmailList = (emailString: string): { valid: boolean; invalidEmails: string[] } => {
  if (!emailString.trim()) return { valid: true, invalidEmails: [] };
  
  const emails = emailString.split(',').map(email => email.trim()).filter(email => email);
  const invalidEmails = emails.filter(email => !isValidEmail(email));
  
  return {
    valid: invalidEmails.length === 0,
    invalidEmails
  };
};

// Función para obtener configuración de la aplicación
const getAppConfig = async (): Promise<AppConfig> => {
  try {
    const response = await fetch('https://backend.anubisai.net/api/pmail/config/app');
    const data = await response.json();
    
    return {
      sizeLimit: data.SIZE_LIMIT ? parseInt(data.SIZE_LIMIT) : DEFAULT_SIZE_LIMIT,
      tempPath: data.TEMP_PATH || '/tmp/database',
      allowedExtensions: data.ALLOWED_EXTENSIONS ? 
        data.ALLOWED_EXTENSIONS.split(',').map((ext: string) => ext.trim()) : 
        ALLOWED_EXTENSIONS
    };
  } catch (error) {
    console.warn('⚠️ Error fetching app config, using defaults:', error);
    return {
      sizeLimit: DEFAULT_SIZE_LIMIT,
      tempPath: '/tmp/database',
      allowedExtensions: ALLOWED_EXTENSIONS
    };
  }
};

// Función para formatear tamaño de archivo
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Función para validar archivo
const validateFile = (file: File, config: AppConfig): { valid: boolean; error?: string } => {
  // Validar tamaño
  if (file.size > config.sizeLimit) {
    return {
      valid: false,
      error: `El archivo es demasiado grande. Máximo permitido: ${formatFileSize(config.sizeLimit)}`
    };
  }
  
  // Validar extensión
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!config.allowedExtensions.includes(fileExtension || '')) {
    return {
      valid: false,
      error: `Tipo de archivo no permitido. Extensiones permitidas: ${config.allowedExtensions.join(', ')}`
    };
  }
  
  return { valid: true };
};

// Función para subir archivo temporal
const uploadTempFile = async (file: File, tempPath: string): Promise<string> => {
  console.log('\n📤 === STARTING UPLOAD (FIXED) ===');
  console.log('📎 File details:', {
    name: file.name,
    size: file.size,
    type: file.type
  });

  try {
    // ✅ CREAR FORMDATA EXACTAMENTE IGUAL que tu código funcionando
    const formData = new FormData();
    formData.append('file', file); // ✅ Sin especificar nombre adicional
    
    // ✅ SOLO SI ES NECESARIO agregar tempPath como campo separado
    // En tu código funcionando NO agregas campos extra, pero si necesitas tempPath:
    if (tempPath && tempPath !== '/tmp/database') {
      formData.append('tempPath', tempPath);
    }

    console.log('📤 FormData created with:', {
      hasFile: formData.has('file'),
      hasTempPath: formData.has('tempPath')
    });

    // ✅ FETCH EXACTAMENTE IGUAL que tu código funcionando
    const response = await fetch('https://backend.anubisai.net/api/pmail/upload-temp', {
      method: 'POST',
      body: formData
      // ✅ NO agregar headers - dejar que el navegador maneje automáticamente
    });

    console.log('📡 Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Response not ok:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Upload successful:', result);
    
    return result.tempPath;

  } catch (error: any) {
    console.error('❌ Upload failed:', error);
    
    // Mejorar mensajes de error
    if (error.message.includes('Unexpected end of form')) {
      throw new Error('Error de transmisión del archivo. Intente nuevamente.');
    }
    
    throw error;
  }
};

// Función alternativa aún MÁS simple (como tu código funcionando)
const uploadTempFileSimple = async (file: File, tempPath: string): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch('https://backend.anubisai.net/api/pmail/upload-temp', {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(error.error || 'Upload failed');
  }

  const result = await response.json();
  return result.tempPath;
};

// Función para probar diferentes endpoints si uno falla
const uploadTempFileWithFallback = async (file: File, tempPath: string): Promise<string> => {
  const endpoints = [
    'https://backend.anubisai.net/api/pmail/upload-temp',
    'https://backend.anubisai.net/api/pmail/upload-temp-busboy' // Si implementas busboy
  ];

  let lastError: Error | null = null;

  for (let i = 0; i < endpoints.length; i++) {
    try {
      console.log(`🔄 Trying endpoint ${i + 1}/${endpoints.length}: ${endpoints[i]}`);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tempPath', tempPath);

      const response = await fetch(endpoints[i], {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Success with endpoint ${i + 1}:`, result);
      return result.tempPath;

    } catch (error: any) {
      console.error(`❌ Endpoint ${i + 1} failed:`, error.message);
      lastError = error;
      
      if (i < endpoints.length - 1) {
        console.log('🔄 Trying next endpoint...');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }

  throw lastError || new Error('Todos los endpoints fallaron');
};

// Función para obtener configuración de email con fallback
const getEmailConfig = (saveDefault: boolean = true) => {
  try {
    const savedConfig = localStorage.getItem('emailConfig');
    
    if (savedConfig) {
      const config = JSON.parse(savedConfig);
      
      if (config.server && config.username && config.password) {
        console.log('📧 Using saved email config for:', config.username);
        return {
          ...DEFAULT_EMAIL_CONFIG,
          ...config
        };
      }
    }
    
    console.log('📧 Using default email config');
    
    if (saveDefault) {
      localStorage.setItem('emailConfig', JSON.stringify(DEFAULT_EMAIL_CONFIG));
      console.log('📧 Default config saved to localStorage');
    }
    
    return DEFAULT_EMAIL_CONFIG;
    
  } catch (error) {
    console.error('Error reading email config:', error);
    console.log('📧 Falling back to default config due to error');
    
    if (saveDefault) {
      localStorage.setItem('emailConfig', JSON.stringify(DEFAULT_EMAIL_CONFIG));
    }
    
    return DEFAULT_EMAIL_CONFIG;
  }
};

const Compose = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtener datos prellenados del state de navegación
  const prefilledData = location.state as {
    to?: string;
    cc?: string;
    subject?: string;
    body?: string;
    originalEmail?: any;
    action?: 'reply' | 'reply-all' | 'forward';
  } || {};
  
  // Estados del formulario
  const [formData, setFormData] = useState<EmailFormData>({
    to: prefilledData.to || '',
    cc: prefilledData.cc || '',
    bcc: '',
    subject: prefilledData.subject || '',
    body: prefilledData.body || ''
  });
  
  // Estados de UI
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [errors, setErrors] = useState<Partial<EmailFormData>>({});
  const [appConfig, setAppConfig] = useState<AppConfig>({
    sizeLimit: DEFAULT_SIZE_LIMIT,
    tempPath: '/tmp/database',
    allowedExtensions: ALLOWED_EXTENSIONS
  });
  
  // Referencias
  const editorRef = useRef<any>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Cargar configuración de la aplicación
  useEffect(() => {
    const loadConfig = async () => {
      const config = await getAppConfig();
      setAppConfig(config);
      console.log('📎 App config loaded:', {
        sizeLimit: formatFileSize(config.sizeLimit),
        tempPath: config.tempPath,
        allowedExtensions: config.allowedExtensions.length + ' extensions'
      });
    };
    
    loadConfig();
  }, []);

  // Efecto para manejar datos prellenados
  useEffect(() => {
    if (prefilledData.to || prefilledData.subject || prefilledData.body) {
      setFormData({
        to: prefilledData.to || '',
        cc: prefilledData.cc || '',
        bcc: '',
        subject: prefilledData.subject || '',
        body: prefilledData.body || ''
      });
      
      if (prefilledData.body && editorRef.current) {
        setTimeout(() => {
          if (editorRef.current && editorRef.current.setContent) {
            editorRef.current.setContent(prefilledData.body);
          }
        }, 100);
      }
    }
  }, [prefilledData]);

  // Manejar cambios en los campos del formulario
  const handleInputChange = (field: keyof EmailFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Manejar cambios en el editor TinyMCE
  const handleEditorChange = (content: string) => {
    setFormData(prev => ({
      ...prev,
      body: content
    }));
  };

  const testRawUpload = async (file: File) => {
  console.log('\n🔬 === TESTING RAW UPLOAD ===');
  
  try {
    // Test 1: Configuración del servidor
    console.log('🔬 Test 1: Server config');
    const configResponse = await fetch('https://backend.anubisai.net/api/pmail/upload-test-config');
    const configData = await configResponse.json();
    console.log('📋 Server config:', configData);
    
    // Test 2: Upload sin Multer
    console.log('🔬 Test 2: Raw upload without Multer');
    const formData = new FormData();
    formData.append('file', file);
    
    const rawResponse = await fetch('https://backend.anubisai.net/api/pmail/upload-test-raw', {
      method: 'POST',
      body: formData
    });
    
    console.log('📡 Raw response status:', rawResponse.status);
    
    if (rawResponse.ok) {
      const rawData = await rawResponse.json();
      console.log('✅ Raw upload successful:', rawData);
      return { success: true, data: rawData };
    } else {
      const errorText = await rawResponse.text();
      console.error('❌ Raw upload failed:', errorText);
      return { success: false, error: errorText };
    }
    
  } catch (error) {
    console.error('❌ Test failed:', error);
    return { success: false, error: error.message };
  }
};

  // Manejar adjuntos de archivos
const handleFileAttachment = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const files = event.target.files;
  if (!files) return;

  console.log('📎 Starting file attachment process:', files.length, 'files');

  const newAttachments: AttachmentFile[] = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const attachmentId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    console.log(`📎 Processing file ${i + 1}:`, {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    
    const validation = validateFile(file, appConfig);
    
    if (!validation.valid) {
      alert(`❌ Error con ${file.name}: ${validation.error}`);
      continue;
    }
    
    const attachment: AttachmentFile = {
      file,
      id: attachmentId,
      status: 'uploading'
    };
    
    newAttachments.push(attachment);
  }
  
  setAttachments(prev => [...prev, ...newAttachments]);
  
  // Procesar cada archivo secuencialmente para evitar sobrecarga
  for (const attachment of newAttachments) {
    if (attachment.status !== 'uploading') continue;
    
    try {
      console.log(`📎 Starting upload for: ${attachment.file.name}`);
      
      // Verificar que el archivo sigue siendo válido
      if (!attachment.file || attachment.file.size === 0) {
        throw new Error('Archivo inválido o vacío');
      }
      
      const tempPath = await uploadTempFile(attachment.file, appConfig.tempPath);
      const testResult = await testRawUpload(attachment.file);
      console.log('🔬 Test result:', testResult);

      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { ...att, status: 'uploaded', tempPath }
          : att
      ));
      
      console.log(`✅ ${attachment.file.name} uploaded successfully to: ${tempPath}`);
      
    } catch (error: any) {
      console.error(`❌ Failed to upload ${attachment.file.name}:`, error);
      
      let errorMessage = error.message || 'Error desconocido';
      
      // Manejar errores específicos
      if (errorMessage.includes('Unexpected end of form')) {
        errorMessage = 'Error de transmisión. Intente con un archivo más pequeño o verifique su conexión.';
      } else if (errorMessage.includes('File too large')) {
        errorMessage = 'El archivo es demasiado grande.';
      } else if (errorMessage.includes('Network')) {
        errorMessage = 'Error de red. Verifique su conexión a internet.';
      }
      
      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { ...att, status: 'error', error: errorMessage }
          : att
      ));
    }
  }
  
  if (event.target) {
    event.target.value = ''; // Limpiar el input
  }
};

  // Remover adjunto
  const removeAttachment = async (attachmentId: string) => {
    const attachment = attachments.find(att => att.id === attachmentId);
    
    if (attachment?.tempPath) {
      try {
        // Eliminar archivo temporal del servidor
        await fetch('https://backend.anubisai.net/api/pmail/delete-temp', {
          method: 'POST', // 🔧 CAMBIAR A POST
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ tempPath: attachment.tempPath })
        });
        console.log(`🗑️ Temp file deleted: ${attachment.tempPath}`);
      } catch (error) {
        console.warn('⚠️ Error deleting temp file:', error);
      }
    }
    
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  // Validar formulario
  const validateForm = (): boolean => {
    const newErrors: Partial<EmailFormData> = {};

    if (!formData.to.trim()) {
      newErrors.to = 'El campo "Para" es obligatorio';
    } else {
      const toValidation = validateEmailList(formData.to);
      if (!toValidation.valid) {
        newErrors.to = `Emails inválidos: ${toValidation.invalidEmails.join(', ')}`;
      }
    }

    if (formData.cc.trim()) {
      const ccValidation = validateEmailList(formData.cc);
      if (!ccValidation.valid) {
        newErrors.cc = `Emails inválidos: ${ccValidation.invalidEmails.join(', ')}`;
      }
    }

    if (formData.bcc.trim()) {
      const bccValidation = validateEmailList(formData.bcc);
      if (!bccValidation.valid) {
        newErrors.bcc = `Emails inválidos: ${bccValidation.invalidEmails.join(', ')}`;
      }
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'El asunto es obligatorio';
    }

    if (!formData.body.trim() || formData.body === '<p></p>' || formData.body === '<p><br></p>') {
      newErrors.body = 'El mensaje no puede estar vacío';
    }

    // Validar que no haya adjuntos con errores
    const failedAttachments = attachments.filter(att => att.status === 'error');
    if (failedAttachments.length > 0) {
      alert(`❌ Hay adjuntos con errores. Por favor, remuévelos antes de enviar.`);
      return false;
    }

    // Validar que no haya adjuntos subiendo
    const uploadingAttachments = attachments.filter(att => att.status === 'uploading');
    if (uploadingAttachments.length > 0) {
      alert(`⏳ Espera a que terminen de subir todos los adjuntos.`);
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar email
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSending(true);

    try {
      const config = getEmailConfig(true);
      console.log('📧 Using email config for sending:', config.username);

      // Preparar adjuntos para envío
      const attachmentPaths = attachments
        .filter(att => att.status === 'uploaded' && att.tempPath)
        .map(att => ({
          filename: att.file.name,
          path: att.tempPath,
          size: att.file.size
        }));

      const emailData = {
        to: formData.to,
        cc: formData.cc,
        bcc: formData.bcc,
        subject: formData.subject,
        body: formData.body,
        attachments: attachmentPaths,
        server: config.server,
        username: config.username,
        password: config.password,
        smtpPort: config.smtpPort,
        ssl: config.ssl,
        displayName: config.displayName
      };

      console.log('📧 Sending email with data:', {
        ...emailData,
        password: '***hidden***',
        attachments: attachmentPaths.map(att => ({
          filename: att.filename,
          size: formatFileSize(att.size)
        }))
      });

      const response = await fetch('https://backend.anubisai.net/api/pmail/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const actionText = prefilledData.action === 'reply' ? 'Respuesta enviada' : 
                          prefilledData.action === 'forward' ? 'Email reenviado' : 
                          'Email enviado';
        
        console.log(`✅ ${actionText} correctamente`);
        
        // Guardar copia en carpeta "Sent"
        try {
          console.log('📤 Saving copy to Sent folder...');
          
          const sentData = {
            server: config.server,
            username: config.username,
            password: config.password,
            imapPort: config.imapPort,
            ssl: config.ssl,
            folderPath: 'Sent',
            email: {
              to: formData.to,
              cc: formData.cc,
              bcc: formData.bcc,
              subject: formData.subject,
              body: formData.body,
              from: `${config.displayName} <${config.username}>`,
              date: new Date().toISOString(),
              attachments: attachmentPaths
            }
          };

          const sentResponse = await fetch('https://backend.anubisai.net/api/pmail/save-to-sent', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(sentData)
          });

          const sentResult = await sentResponse.json();
          
          if (sentResponse.ok && sentResult.success) {
            console.log('📤 Email saved to Sent folder successfully');
          } else {
            console.warn('⚠️ Failed to save to Sent folder:', sentResult.error);
          }
          
        } catch (sentError: any) {
          console.warn('⚠️ Error saving to Sent folder:', sentError.message);
        }
        
        // Limpiar archivos temporales
        for (const attachment of attachments) {
          if (attachment.tempPath) {
            try {
              await fetch('https://backend.anubisai.net/api/pmail/delete-temp', {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ tempPath: attachment.tempPath })
              });
            } catch (error) {
              console.warn('⚠️ Error cleaning temp file:', error);
            }
          }
        }
        
        alert(`✅ ${actionText} correctamente`);
        
        // Limpiar formulario
        setFormData({
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          body: ''
        });
        setAttachments([]);
        
        navigate('/anubis/email/inbox');
      } else {
        throw new Error(result.error || 'Error al enviar el email');
      }

    } catch (error: any) {
      console.error('Error sending email:', error);
      alert(`❌ Error al enviar email: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  // Descartar borrador
  const handleDiscard = async () => {
    if (formData.to || formData.subject || formData.body.trim() || attachments.length > 0) {
      const confirmDiscard = window.confirm('¿Estás seguro de que quieres descartar este borrador?');
      if (!confirmDiscard) return;
    }
    
    // Limpiar archivos temporales
    for (const attachment of attachments) {
      if (attachment.tempPath) {
        try {
          await fetch('https://backend.anubisai.net/api/pmail/delete-temp', {
            method: 'POST', // 🔧 CAMBIAR A POST
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ tempPath: attachment.tempPath })
          });
        } catch (error) {
          console.warn('⚠️ Error cleaning temp file:', error);
        }
      }
    }
    
    // Limpiar formulario
    setFormData({
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body: ''
    });
    setAttachments([]);
    setErrors({});
    
    navigate('/anubis/email/inbox');
  };

  // Calcular tamaño total de adjuntos
  const totalAttachmentSize = attachments.reduce((total, att) => total + att.file.size, 0);

  return (
    <EmailLayout page="compose">
      <Col>
        <Card className="email-content">
          <Card.Body>
            <form className="d-flex flex-column h-100" onSubmit={handleSubmit}>
              <Row className="g-3 mb-2">
                <Col xs={4}>
                  <Form.Control 
                    type="email" 
                    placeholder="Para (obligatorio)"
                    value={formData.to}
                    onChange={(e) => handleInputChange('to', e.target.value)}
                    isInvalid={!!errors.to}
                    disabled={sending}
                    multiple
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.to}
                  </Form.Control.Feedback>
                </Col>
                <Col xs={4}>
                  <Form.Control 
                    type="email" 
                    placeholder="CC"
                    value={formData.cc}
                    onChange={(e) => handleInputChange('cc', e.target.value)}
                    isInvalid={!!errors.cc}
                    disabled={sending}
                    multiple
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.cc}
                  </Form.Control.Feedback>
                </Col>
                <Col xs={4}>
                  <Form.Control 
                    type="email" 
                    placeholder="BCC"
                    value={formData.bcc}
                    onChange={(e) => handleInputChange('bcc', e.target.value)}
                    isInvalid={!!errors.bcc}
                    disabled={sending}
                    multiple
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.bcc}
                  </Form.Control.Feedback>
                </Col>
                <Col xs={12}>
                  <Form.Control 
                    type="text" 
                    placeholder="Asunto (obligatorio)"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    isInvalid={!!errors.subject}
                    disabled={sending}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.subject}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              {/* Lista de adjuntos */}
              {attachments.length > 0 && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">
                      Adjuntos ({attachments.length}) - {formatFileSize(totalAttachmentSize)}
                    </small>
                    <small className="text-muted">
                      Máximo: {formatFileSize(appConfig.sizeLimit)}
                    </small>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {attachments.map((attachment) => (
                      <div 
                        key={attachment.id}
                        className={`d-flex align-items-center justify-content-between p-2 rounded border ${
                          attachment.status === 'error' ? 'border-danger bg-light-danger' :
                          attachment.status === 'uploading' ? 'border-warning bg-light-warning' :
                          'border-success bg-light-success'
                        }`}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {attachment.status === 'uploading' && (
                            <Spinner 
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                          )}
                          <div>
                            <div className="fw-medium fs-9">{attachment.file.name}</div>
                            <div className="text-muted fs-10">
                              {formatFileSize(attachment.file.size)}
                              {attachment.status === 'uploading' && ' - Subiendo...'}
                              {attachment.status === 'uploaded' && ' - ✅ Listo'}
                              {attachment.status === 'error' && ` - ❌ ${attachment.error}`}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-1 text-danger"
                          onClick={() => removeAttachment(attachment.id)}
                          disabled={sending}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {totalAttachmentSize > appConfig.sizeLimit && (
                    <Alert variant="danger" className="mt-2 mb-0">
                      <small>
                        ⚠️ El tamaño total de adjuntos excede el límite permitido de {formatFileSize(appConfig.sizeLimit)}
                      </small>
                    </Alert>
                  )}
                </div>
              )}

              <div className="mb-3 flex-1">
                <TinymceEditor
                  value={formData.body}
                  onChange={handleEditorChange}
                  options={{
                    height: '100%',
                    setup: (editor: any) => {
                      editor.on('init', () => {
                        if (prefilledData.body) {
                          editor.setContent(prefilledData.body);
                        }
                      });
                    }
                  }}
                />
                {errors.body && (
                  <div className="text-danger fs-9 mt-1">{errors.body}</div>
                )}
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-3 align-items-center">
                  <div>
                    <Button 
                      className="p-0"
                      disabled={sending}
                      onClick={() => attachmentInputRef.current?.click()}
                      title="Adjuntar archivos"
                    >
                      <label className="text-body fs-9 cursor-pointer">
                        <FontAwesomeIcon icon={faPaperclip} />
                      </label>
                    </Button>
                    <Form.Control
                      ref={attachmentInputRef}
                      className="d-none"
                      type="file"
                      onChange={handleFileAttachment}
                      multiple
                      accept={appConfig.allowedExtensions.join(',')}
                    />
                  </div>

                  <div>
                    <Button 
                      className="p-0"
                      disabled={sending}
                      onClick={() => imageInputRef.current?.click()}
                      title="Adjuntar imágenes"
                    >
                      <label className="text-body fs-9 cursor-pointer">
                        <FontAwesomeIcon icon={faImage} />
                      </label>
                    </Button>
                    <Form.Control
                      ref={imageInputRef}
                      className="d-none"
                      type="file"
                      accept="image/*"
                      onChange={handleFileAttachment}
                      multiple
                    />
                  </div>
                  
                  <div className="text-muted fs-10">
                    <small>
                      Máximo: {formatFileSize(appConfig.sizeLimit)} por email
                    </small>
                  </div>
                </div>
                
                <div className="d-flex">
                  <Button
                    variant="link"
                    className="text-body fs-10 text-decoration-none"
                    onClick={handleDiscard}
                    disabled={sending}
                  >
                    Descartar
                  </Button>
                  <Button
                    variant="primary"
                    className="fs-10"
                    type="submit"
                    disabled={sending || totalAttachmentSize > appConfig.sizeLimit}
                    endIcon={
                      sending ? (
                        <Spinner 
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <FontAwesomeIcon icon={faPaperPlane} />
                      )
                    }
                  >
                    {sending ? 'Enviando...' : 'Enviar'}
                  </Button>
                </div>
              </div>
            </form>
          </Card.Body>
        </Card>
      </Col>
    </EmailLayout>
  );
};

export default Compose;